import { Injectable, Injector } from '@angular/core';
import { BaseState } from 'src/app/common/base/base.state'; import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { DocumentsCreate, DocumentsDelete, DocumentsRefresh, DocumentsUpdate, DocumentsRequestRefresh, DocumentsUpdateAll } from './documents.action';
import { Document } from '../documents';
import { DocumentsService } from '../documents.service';
import { utils } from 'src/app/modules/libs/utils';
import { FormType } from 'src/app/common/enumerations/enumerations';
import { EMPTY, Observable } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';


export class DocumentsStateModel {
  data: Document[] = [];
  // Prevent consumers from treating the initial empty array as a completed refresh.
  loaded: boolean = false;
}

@State<DocumentsStateModel>({
  name: 'Documents',
  defaults: {
    data: [],
    loaded: false,
  }
})
@Injectable()
export class DocumentsState extends BaseState {
  private refreshRequest$?: Observable<Document[]>;

  constructor(
    protected override injector: Injector,
    private _service: DocumentsService,
    private store: Store

  ) {
    super(injector);
  }

  @Selector()
  static read(state: DocumentsStateModel) {
    return state.data;
  }

  /**
   * Loads the full document list with a shared in-flight request.
   * Multiple refresh actions can be dispatched during routing, but only one `/Documents` GET should run at a time.
   */
  @Action(DocumentsRefresh)
  refresh({ patchState }: StateContext<DocumentsStateModel>) {
    patchState({ loaded: false });
    if (!this.refreshRequest$) {
      // Share the active refresh so redirect retries do not start duplicate HTTP requests.
      this.refreshRequest$ = this._service.getDocuments().pipe(
        tap(data => {
          this.setDescriptions(data);
          patchState({ data: this.sortBySerialNo(data), loaded: true });
        }),
        catchError(error => {
          console.error(error);
          return EMPTY;
        }),
        finalize(() => this.refreshRequest$ = undefined),
        shareReplay(1)
      );
    }

    return this.refreshRequest$;
  }

  @Action(DocumentsRequestRefresh)
  refreshById({ getState, patchState }: StateContext<DocumentsStateModel>, { item }: DocumentsRequestRefresh) {
    this._service.getDocument(item).subscribe(
      data => {
        const state = getState();
        if (data.id) {
          const index = state.data.map(x => x.id).indexOf(data.id);

          if (index >= 0)
            state.data.splice(index, 1);

          this.setDescriptions([data]);
          patchState({
            data: this.sortBySerialNo([...state.data, data])
          });
        }
        else {
          this.store.dispatch(new DocumentsDelete(item))
        }
      },
      error => console.error(error)
    );
  }

  @Action(DocumentsCreate)
  create({ getState, patchState }: StateContext<DocumentsStateModel>, { item }: DocumentsCreate) {
    const state = getState();
    const index = state.data.map(x => x.id).indexOf(item.id);
    if (index >= 0) {
      state.data.splice(index, 1);
    }
    this.setDescriptions([item]);
    patchState({
      data: this.sortBySerialNo([...getState().data, item])
    });
  }

  @Action(DocumentsUpdate)
  update({ getState, patchState }: StateContext<DocumentsStateModel>, { id, item }: DocumentsUpdate) {
    const state = getState();
    const index = state.data.map(x => x.id).indexOf(id);

    if (index >= 0) {
      state.data.splice(index, 1);
      this.setDescriptions([item]);
      patchState({
        data: this.sortBySerialNo([...state.data, item])
      });
    }
  }

  /**
   * Orders documents by serial number descending so newer forms lead their type group.
   * The full load already sorted this way; SignalR single-document paths appended instead, pushing new forms to the bottom.
   */
  private sortBySerialNo(documents: Document[]): Document[] {
    return documents.sort((a, b) => utils.sort(a.serialNo, b.serialNo, false));
  }

  @Action(DocumentsUpdateAll)
  updateAll({ patchState }: StateContext<DocumentsStateModel>, { data }: DocumentsUpdateAll) {
    this.setDescriptions(data);
    patchState({ data });
  }

  @Action(DocumentsDelete)
  delete({ getState, patchState }: StateContext<DocumentsStateModel>, { item }: DocumentsDelete) {
    // Emit a new array so store selectors notice the removal; an in-place splice keeps the same reference and is skipped.
    patchState({
      data: getState().data.filter(d => !(d.formID == item.id && d.type == item.formType && d.category == item.category))
    });
  }

  setDescriptions(documents: Document[]) {
    documents?.map(d => {
      switch (d.type) {
        case FormType.KE:
          d.description = 'Affects ' + d.locations?.map(l => l.name).join(', ');
          break;
        case FormType.ABSI:
          d.description = 'ABSI Checklist for ' + d.locations?.map(l => l.name).join(', ');
          break;
        default:
          d.description = this.stripHtml(d.description) + (d.locationIDs ? (' (' + d.locations?.map(l => l.name).join(', ') + ')') : '');
      }
    });
  }

  stripHtml(html: any) {
    let doc = new DOMParser().parseFromString(html, "text/html");
    if (doc.body.textContent == 'undefined') doc.body.textContent = '';
    return doc.body.textContent || "";
  }

}
